import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from 'lucide-react';
import * as React from 'react';
import {
  type DayButton,
  DayPicker,
  type DropdownProps,
  getDefaultClassNames,
  type Locale,
} from 'react-day-picker';

import { cn } from '../../lib/utils';
import { Button, buttonVariants } from './button';
import { Icon } from './icon';

/**
 * 生日日历左右翻月：跟会话设置顶栏 `BackButton` 同一张 `BG - Small` 玻璃圆面
 * （40 圆、0.75 描边、95% 面、落影 0 2 6 / 4%、hover 洗一层白）。不挂 backdrop-filter。
 * 95% 面平台没有 a95 档，与 `--im-header-button` 同样留字面值。
 * 箭头用稿 `i-chevron-*`（24、stroke 1.6、currentColor）：默认 `Color/text/quaternary`，
 * hover `Color/text/brand`。
 */
const CALENDAR_NAV_FACE =
  'inline-flex size-[40px] shrink-0 cursor-pointer items-center justify-center rounded-full border-[0.75px] border-[var(--app-base-white-a60)] bg-[rgba(255,255,255,0.95)] p-0 text-(--app-color-text-quaternary) shadow-[0_2px_6px_rgba(0,0,0,0.04)] select-none hover:bg-[var(--app-base-white-a40)] hover:text-(--app-color-text-brand) aria-disabled:opacity-45 dark:border-[color-mix(in_srgb,white_14%,transparent)] dark:bg-[rgba(38,38,38,0.95)] dark:hover:bg-[var(--app-base-white-a10)]';

/** 稿选中胶囊 34.2×27.2；列宽跟它走，格子不再被弹层拉宽。 */
const BIRTHDAY_DAY_W = 'w-[34.2px]';
const BIRTHDAY_DAY_H = 'h-[27.2px]';

/** rdp 默认用 table；生日格子要 10px gap + 固定胶囊宽，必须换成 flex。 */
function BirthdayMonthGrid(props: React.ComponentProps<'table'>) {
  return <div {...props} />;
}

function BirthdayWeekdays(props: React.ComponentProps<'tr'>) {
  return <div {...props} />;
}

function BirthdayWeekday({
  scope: _scope,
  ...props
}: React.ComponentProps<'th'>) {
  return <div {...props} />;
}

function BirthdayWeeks(props: React.ComponentProps<'tbody'>) {
  return <div {...props} />;
}

function BirthdayWeek({
  week: _week,
  ...props
}: React.ComponentProps<'tr'> & { week?: unknown }) {
  return <div {...props} />;
}

function BirthdayDay({
  day: _day,
  modifiers: _modifiers,
  ...props
}: React.ComponentProps<'td'> & { day?: unknown; modifiers?: unknown }) {
  return <div {...props} />;
}

/**
 * 年月下拉（稿 `9:14296`）：月名在上、年份在下；点年份弹出窄列表，选中行是胶囊底。
 * 列表画在日历卡片里，避免再开一层 portal 把外层 DatePicker Popover 点收掉。
 */
function CalendarDropdown({
  options,
  value,
  onChange,
  disabled,
  className,
  'aria-label': ariaLabel,
}: DropdownProps) {
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef<HTMLSpanElement>(null);
  const selected = options?.find((option) => option.value === Number(value));
  const isYear = Boolean(options?.some((option) => option.value > 31));

  React.useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (event: PointerEvent): void => {
      if (rootRef.current?.contains(event.target as Node)) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () =>
      document.removeEventListener('pointerdown', onPointerDown, true);
  }, [open]);

  const pick = (next: number): void => {
    onChange?.({
      target: { value: String(next) },
    } as React.ChangeEvent<HTMLSelectElement>);
    setOpen(false);
  };

  return (
    <span ref={rootRef} className="relative">
      <button
        type="button"
        data-slot={isYear ? 'calendar-year-trigger' : undefined}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen((current) => !current)}
        className={cn(
          'cursor-pointer bg-transparent outline-none disabled:cursor-not-allowed disabled:opacity-50',
          isYear
            ? 'text-xs leading-4 text-muted-foreground'
            : 'text-sm leading-5 font-medium',
          className,
        )}
      >
        {selected?.label}
      </button>
      {open ? (
        <div
          data-slot="calendar-year-list"
          role="listbox"
          aria-label={ariaLabel}
          className="absolute top-[calc(100%+8px)] left-1/2 z-20 max-h-[280px] w-[128px] -translate-x-1/2 overflow-y-auto rounded-2xl bg-popover p-1.5 text-popover-foreground shadow-[0_6px_26.6px_rgba(0,0,0,0.12)]"
        >
          {options?.map((option) => {
            const active = option.value === Number(value);
            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={active}
                disabled={option.disabled}
                onClick={() => pick(option.value)}
                className={cn(
                  'flex h-9 w-full cursor-pointer items-center rounded-[10px] px-3 text-left text-sm outline-none disabled:cursor-not-allowed disabled:opacity-50',
                  active
                    ? 'bg-muted text-foreground'
                    : 'text-foreground hover:bg-muted/60',
                )}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      ) : null}
    </span>
  );
}

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = 'label',
  buttonVariant = 'ghost',
  navLayout,
  locale,
  formatters,
  components,
  ...props
}: React.ComponentProps<typeof DayPicker> & {
  buttonVariant?: React.ComponentProps<typeof Button>['variant'];
}) {
  const defaultClassNames = getDefaultClassNames();
  const dropdownCaption = captionLayout !== 'label';

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn(
        'group/calendar bg-background p-3 [--cell-radius:var(--radius-md)] [--cell-size:2rem] in-data-[slot=card-content]:bg-transparent in-data-[slot=popover-content]:bg-transparent',
        dropdownCaption && 'w-max bg-popover p-4',
        String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
        String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
        className,
      )}
      captionLayout={captionLayout}
      navLayout={navLayout}
      locale={locale}
      formatters={{
        formatMonthDropdown: (date) =>
          date.toLocaleString(locale?.code, { month: 'long' }),
        ...formatters,
      }}
      classNames={{
        root: cn('w-fit', defaultClassNames.root),
        months: cn(
          'relative flex flex-col gap-4 md:flex-row',
          defaultClassNames.months,
        ),
        month: cn(
          dropdownCaption
            ? 'flex w-max flex-col gap-3'
            : 'flex w-full flex-col gap-4',
          defaultClassNames.month,
        ),
        nav: cn(
          dropdownCaption
            ? 'absolute inset-x-0 top-0 z-10 flex h-[40px] w-full items-center justify-between pointer-events-none'
            : 'absolute inset-x-0 top-0 z-10 flex h-10 w-full items-center justify-between',
          defaultClassNames.nav,
        ),
        button_previous: cn(
          dropdownCaption
            ? `${CALENDAR_NAV_FACE} pointer-events-auto`
            : cn(
                buttonVariants({ variant: buttonVariant }),
                'size-(--cell-size) p-0 select-none aria-disabled:opacity-50',
              ),
          defaultClassNames.button_previous,
        ),
        button_next: cn(
          dropdownCaption
            ? `${CALENDAR_NAV_FACE} pointer-events-auto`
            : cn(
                buttonVariants({ variant: buttonVariant }),
                'size-(--cell-size) p-0 select-none aria-disabled:opacity-50',
              ),
          defaultClassNames.button_next,
        ),
        month_caption: cn(
          dropdownCaption
            ? 'flex min-h-[40px] w-full cursor-pointer flex-col items-center justify-center px-11'
            : 'flex h-(--cell-size) w-full items-center justify-center px-(--cell-size)',
          defaultClassNames.month_caption,
        ),
        dropdowns: cn(
          dropdownCaption
            ? 'flex w-full flex-col items-center justify-center gap-0 [&>span]:text-sm [&>span]:leading-5 [&>span]:font-medium'
            : 'flex h-(--cell-size) w-full items-center justify-center gap-1.5 text-sm font-medium',
          defaultClassNames.dropdowns,
        ),
        dropdown_root: cn('relative', defaultClassNames.dropdown_root),
        dropdown: cn(defaultClassNames.dropdown),
        caption_label: cn(
          'select-none',
          captionLayout === 'label'
            ? 'text-sm font-medium'
            : 'text-sm leading-5 font-medium',
          defaultClassNames.caption_label,
        ),
        month_grid: cn(
          dropdownCaption ? 'w-max' : 'w-full border-collapse',
          defaultClassNames.month_grid,
        ),
        weekdays: cn(
          dropdownCaption ? 'flex w-max capitalize gap-[10px]' : 'flex',
          defaultClassNames.weekdays,
        ),
        weekday: cn(
          dropdownCaption
            ? `${BIRTHDAY_DAY_W} shrink-0 truncate text-center text-[12px] leading-4 font-normal text-muted-foreground select-none`
            : 'min-w-0 flex-1 truncate text-center text-[0.8rem] font-normal text-muted-foreground select-none',
          defaultClassNames.weekday,
        ),
        weeks: cn(
          dropdownCaption ? 'mt-[10px] flex flex-col gap-[10px]' : undefined,
          defaultClassNames.weeks,
        ),
        week: cn(
          dropdownCaption ? 'flex w-max gap-[10px]' : 'mt-2 flex w-full',
          defaultClassNames.week,
        ),
        week_number_header: cn(
          'w-(--cell-size) select-none',
          defaultClassNames.week_number_header,
        ),
        week_number: cn(
          'text-[0.8rem] text-muted-foreground select-none',
          defaultClassNames.week_number,
        ),
        day: cn(
          dropdownCaption
            ? `group/day relative flex ${BIRTHDAY_DAY_H} ${BIRTHDAY_DAY_W} shrink-0 items-center justify-center p-0 text-center select-none`
            : 'group/day relative flex aspect-square h-full w-full items-center justify-center p-0 text-center select-none',
          defaultClassNames.day,
        ),
        range_start: cn(
          'relative isolate z-0 rounded-l-(--cell-radius) bg-muted after:absolute after:inset-y-0 after:right-0 after:w-4 after:bg-muted',
          defaultClassNames.range_start,
        ),
        range_middle: cn('rounded-none', defaultClassNames.range_middle),
        range_end: cn(
          'relative isolate z-0 rounded-r-(--cell-radius) bg-muted after:absolute after:inset-y-0 after:left-0 after:w-4 after:bg-muted',
          defaultClassNames.range_end,
        ),
        today: cn('text-foreground', defaultClassNames.today),
        outside: cn(
          'text-muted-foreground aria-selected:text-muted-foreground',
          defaultClassNames.outside,
        ),
        disabled: cn(
          'text-muted-foreground opacity-50',
          defaultClassNames.disabled,
        ),
        hidden: cn('invisible', defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Dropdown: CalendarDropdown,
        MonthCaption: ({
          calendarMonth: _month,
          displayIndex: _index,
          ...captionProps
        }) => (
          <div
            {...captionProps}
            onClick={(event) => {
              captionProps.onClick?.(event);
              if (event.defaultPrevented) {
                return;
              }
              const target = event.target;
              if (!(target instanceof Element) || target.closest('button')) {
                return;
              }
              const trigger = event.currentTarget.querySelector(
                '[data-slot=calendar-year-trigger]',
              );
              if (trigger instanceof HTMLElement) {
                trigger.click();
              }
            }}
          />
        ),
        Root: ({ className, rootRef, ...rootProps }) => (
          <div
            data-slot="calendar"
            ref={rootRef}
            className={cn(className)}
            {...rootProps}
          />
        ),
        Chevron: ({ className, orientation, ...chevronProps }) => {
          if (
            dropdownCaption &&
            (orientation === 'left' || orientation === 'right')
          ) {
            return (
              <Icon
                spec={{
                  type: 'builtin',
                  id:
                    orientation === 'left'
                      ? 'i-chevron-left'
                      : 'i-chevron-right',
                }}
                size={24}
                className={cn('size-6', className)}
              />
            );
          }
          const Chevron =
            orientation === 'left'
              ? ChevronLeftIcon
              : orientation === 'right'
                ? ChevronRightIcon
                : ChevronDownIcon;
          return (
            <Chevron
              className={cn(dropdownCaption ? 'size-6' : 'size-4', className)}
              {...chevronProps}
            />
          );
        },
        ...(dropdownCaption
          ? {
              MonthGrid: BirthdayMonthGrid,
              Weekdays: BirthdayWeekdays,
              Weekday: BirthdayWeekday,
              Weeks: BirthdayWeeks,
              Week: BirthdayWeek,
              Day: BirthdayDay,
            }
          : {}),
        DayButton: (dayButtonProps) => (
          <CalendarDayButton
            locale={locale}
            compact={dropdownCaption}
            {...dayButtonProps}
          />
        ),
        WeekNumber: ({ children, ...weekNumberProps }) => (
          <td {...weekNumberProps}>
            <div className="flex size-(--cell-size) items-center justify-center text-center">
              {children}
            </div>
          </td>
        ),
        ...components,
      }}
      {...props}
    />
  );
}

function CalendarDayButton({
  className,
  day,
  modifiers,
  locale,
  compact = false,
  ...props
}: React.ComponentProps<typeof DayButton> & {
  locale?: Partial<Locale>;
  compact?: boolean;
}) {
  const defaultClassNames = getDefaultClassNames();
  const ref = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => {
    if (modifiers.focused) {
      ref.current?.focus();
    }
  }, [modifiers.focused]);

  return (
    <Button
      ref={ref}
      variant="ghost"
      size="icon"
      data-day={day.date.toLocaleDateString(locale?.code)}
      data-selected-single={
        modifiers.selected &&
        !modifiers.range_start &&
        !modifiers.range_end &&
        !modifiers.range_middle
      }
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      data-today={modifiers.today || undefined}
      className={cn(
        compact
          ? // 稿选中态 34.2×27.2、圆角 13.6（两端半圆胶囊，不是正圆）。当天 / hover 同一颗。
            `relative isolate z-10 size-auto ${BIRTHDAY_DAY_H} ${BIRTHDAY_DAY_W} min-w-[34.2px] rounded-[13.6px] border-0 p-0 text-sm leading-none font-normal hover:bg-muted hover:text-foreground group-data-[focused=true]/day:z-10 group-data-[focused=true]/day:border-ring group-data-[focused=true]/day:ring-3 group-data-[focused=true]/day:ring-ring/50 data-[today=true]:bg-muted data-[selected-single=true]:bg-primary data-[selected-single=true]:text-primary-foreground data-[selected-single=true]:hover:bg-primary data-[selected-single=true]:hover:text-primary-foreground`
          : 'flex aspect-square size-auto w-full min-w-(--cell-size) flex-col gap-1 leading-none font-normal group-data-[focused=true]/day:relative group-data-[focused=true]/day:z-10 group-data-[focused=true]/day:border-ring group-data-[focused=true]/day:ring-3 group-data-[focused=true]/day:ring-ring/50 data-[range-end=true]:rounded-(--cell-radius) data-[range-end=true]:rounded-r-(--cell-radius) data-[range-end=true]:bg-primary data-[range-end=true]:text-primary-foreground data-[range-middle=true]:rounded-none data-[range-middle=true]:bg-muted data-[range-middle=true]:text-foreground data-[range-start=true]:rounded-(--cell-radius) data-[range-start=true]:rounded-l-(--cell-radius) data-[range-start=true]:bg-primary data-[range-start=true]:text-primary-foreground data-[selected-single=true]:bg-primary data-[selected-single=true]:text-primary-foreground dark:hover:text-foreground [&>span]:text-xs [&>span]:opacity-70',
        defaultClassNames.day,
        className,
      )}
      {...props}
    />
  );
}

export { Calendar, CalendarDayButton };
